/**
 * W-R5 (DISC-AC-146, AC-H-8 render arm) — ONE resolver for how the paid
 * spend/CAC/ROAS cells present a withheld spend denominator.
 *
 * `policy_missing` is a DISTINCT typed state (AC-H-8): mixed ad-account
 * currencies with NO registered money-reporting policy — the register CTA is
 * the lever. A mixed row whose payload carries a policy version is a
 * different state (the decision lane owns cross-currency math); prescribing
 * "register a policy" there would be a lie. Server truth signal:
 * `decisionFxPolicyVersion` rides every campaign row when a policy resolved.
 */

export type SpendMoneyPresentationState =
  | 'ok'
  | 'no_spend'
  | 'policy_missing'
  | 'mixed_currency_policy_bound'
  | 'missing_currency'
  | 'legacy_incomplete';

export interface SpendMoneyPresentation {
  state: SpendMoneyPresentationState;
  /** Hover title for the ROAS / ROAS(LTV) cells; undefined = lane default. */
  reasonTitle?: string;
  /** Warning caption under the withheld spend value; undefined = none. */
  spendCaption?: string;
  /** Short phrase for the identity tooltip row; undefined = no row. */
  tooltipValue?: string;
}

export function resolveSpendMoneyPresentation(row: {
  spendMoneyState?: string | null;
  decisionFxPolicyVersion?: string | null;
}): SpendMoneyPresentation {
  const state = row.spendMoneyState;
  if (state === 'mixed_currency') {
    if (!row.decisionFxPolicyVersion) {
      return {
        state: 'policy_missing',
        reasonTitle: 'Mixed ad-account currencies and no money-reporting (FX) policy is registered — the spend denominator is withheld rather than summed across currencies. Register a policy for this project to unlock.',
        spendCaption: 'mixed currencies · reporting policy not configured',
        tooltipValue: 'mixed currencies · reporting policy not configured',
      };
    }
    return {
      state: 'mixed_currency_policy_bound',
      reasonTitle: `Mixed ad-account currencies — the native sum is withheld; policy ${row.decisionFxPolicyVersion} converts decision metrics in the reporting currency.`,
      spendCaption: 'mixed ad-account currencies — sum withheld',
      tooltipValue: 'mixed ad-account currencies',
    };
  }
  if (state === 'missing_currency') {
    return {
      state: 'missing_currency',
      reasonTitle: 'Spend currency evidence missing — the provider rows carry no currency metadata.',
      spendCaption: 'spend currency evidence missing',
      tooltipValue: 'spend currency evidence missing',
    };
  }
  if (state === 'no_spend') return { state: 'no_spend' };
  if (state === 'single_currency') return { state: 'ok' };
  // Legacy cached payloads (undefined/null state) keep the generic caption.
  return { state: 'legacy_incomplete', spendCaption: 'FX/currency evidence incomplete' };
}
